import { Table, Tag, Avatar, Button, Space, Tooltip } from '@/shared/ui/primitives';
import { EditOutlined, DeleteOutlined, EyeOutlined, StopOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@/shared/ui/icons';
import { formatDateTime } from '@/shared/utils/formatters';
import { useTranslation } from 'react-i18next';
import type { ColumnsType, TableProps } from '@/shared/ui/subpath-types';

interface Props {
  data: any[];
  loading: boolean;
  sortField: string;
  sortDir: string;
  onSort: (field: string) => void;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (item: any) => void;
  onStatusChange?: (item: any, status: 'ACTIVE' | 'SUSPENDED' | 'UNDER_REVIEW') => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  selectedIds?: number[];
  onSelectionChange?: (ids: number[]) => void;
}

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'green',
  SUSPENDED: 'red',
  UNDER_REVIEW: 'orange',
};

export default function UserTable({ data, loading, onSort, onView, onEdit, onDelete, onStatusChange, page, pageSize, total, onPageChange, selectedIds, onSelectionChange }: Props) {
  const { t } = useTranslation();

  const columns: ColumnsType<any> = [
    { title: t('common.id'), dataIndex: 'id', sorter: true, width: 80 },
    {
      title: t('users.name'), dataIndex: 'firstName', sorter: true,
      render: (_: any, record: any) => (
        <Space>
          {record.avatar && <Avatar src={record.avatar} size="small" />}
          <span>{record.firstName} {record.lastName}</span>
        </Space>
      ),
    },
    { title: t('users.email'), dataIndex: 'email', sorter: true, render: (v: string) => <span data-allstak-mask data-sensitive>{v || '—'}</span> },
    {
      title: t('users.phone'), dataIndex: 'phoneNumber', sorter: true,
      render: (_: any, record: any) => (
        <span data-allstak-mask data-sensitive>
          {record.phoneNumber ? `${record.phonePrefix || ''}${record.phoneNumber}` : '—'}
        </span>
      ),
    },
    { title: t('users.gender'), dataIndex: 'gender', sorter: true, render: (v: string) => v || '—' },
    {
      title: t('users.verified'), dataIndex: 'isPhoneVerified', sorter: true, width: 100,
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('common.status'), width: 150,
      render: (_: any, record: any) => {
        if (record.isDeleted) return <Tag color="red">{t('accountStatus.archived')}</Tag>;
        const s = record.accountStatus || 'ACTIVE';
        const label = t(`accountStatus.${s}`, { defaultValue: s }) as string;
        return <Tag color={STATUS_COLOR[s] ?? 'default'}>{label}</Tag>;
      },
    },
    {
      title: t('common.createdAt'), dataIndex: 'createdAt', sorter: true,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t('common.actions'), width: 210,
      render: (_: any, record: any) => {
        const isSuspended = record.accountStatus === 'SUSPENDED';
        const isReview = record.accountStatus === 'UNDER_REVIEW';
        return (
          <Space>
            <Tooltip title={t('userOps.tooltip.view')}><Button type="text" size="small" icon={<EyeOutlined />} onClick={() => onView(record.id)} /></Tooltip>
            <Tooltip title={t('userOps.tooltip.edit')}><Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record.id)} /></Tooltip>
            {onStatusChange && !record.isDeleted && (
              isSuspended
                ? <Tooltip title={t('userOps.tooltip.reactivate')}><Button type="text" size="small" icon={<CheckCircleOutlined />} style={{ color: 'var(--success)' }} onClick={() => onStatusChange(record, 'ACTIVE')} /></Tooltip>
                : <Tooltip title={t('userOps.tooltip.suspend')}><Button type="text" size="small" icon={<StopOutlined />} danger onClick={() => onStatusChange(record, 'SUSPENDED')} /></Tooltip>
            )}
            {onStatusChange && !record.isDeleted && !isReview && !isSuspended && (
              <Tooltip title={t('userOps.tooltip.flag')}><Button type="text" size="small" icon={<ExclamationCircleOutlined />} style={{ color: 'var(--warning)' }} onClick={() => onStatusChange(record, 'UNDER_REVIEW')} /></Tooltip>
            )}
            {!record.isDeleted && (
              <Tooltip title={t('userOps.tooltip.archive')}><Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)} /></Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  const handleChange: TableProps['onChange'] = (_pagination, _filters, sorter: any) => {
    if (sorter?.field) onSort(sorter.field);
  };

  const rowSelection = onSelectionChange
    ? {
        selectedRowKeys: selectedIds ?? [],
        onChange: (keys: React.Key[]) => onSelectionChange(keys.map((k) => Number(k))),
        getCheckboxProps: (record: any) => ({ disabled: record.isDeleted }),
      }
    : undefined;

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      onChange={handleChange}
      sortDirections={['ascend', 'descend']}
      showSorterTooltip={false}
      rowSelection={rowSelection}
      pagination={{
        current: page + 1,
        pageSize,
        total,
        showTotal: (total) => `${total} items`,
        showSizeChanger: false,
        onChange: (p) => onPageChange(p - 1),
      }}
    />
  );
}
